/* eslint-disable react/no-danger */
import type { ReactElement } from 'react';

import { useMemo } from 'react';
import styled from 'styled-components';

import { usePDPContext } from '../../../utilities/context/static/PDPContext';
import { WashingLabels } from '../productInfo/WashingLabels';
import {
  AccordionElementColorConfig,
  AccordionLinkColorConfig,
} from '../sustainabilityTraceability/styles';
import { FesLink } from '~/components/shared/core/link/FesLink';
import { getColorDetailUrl } from '~/utilities/helpers/getColorDetailUrl';

const S = {
  Wrapper: styled.div`
    font-weight: ${({ theme }) => theme.variables.font.weight.regular};
    line-height: 1.4;

    ${({ theme }) => AccordionElementColorConfig[theme.siteType]}

    h2 {
      margin-bottom: ${({ theme }) => theme.spacing.y.md};
      line-height: 1.4;
      font-size: ${({ theme }) => theme.font.size[3]};
    }

    h3 {
      font-weight: ${({ theme }) => theme.variables.font.weight.medium};
      font-size: ${({ theme }) => theme.font.size[3]};
      line-height: 1.4;
      margin-top: ${({ theme }) => theme.spacing.y.md};
    }

    p {
      margin: ${({ theme }) => theme.spacing.y.xs} 0;
      margin-bottom: 0;
      font-size: ${({ theme }) => theme.font.size[3]};
    }

    p:not(:last-of-type) {
      margin-bottom: ${({ theme }) => theme.spacing.y.xs};
      font-size: ${({ theme }) => theme.font.size[3]};
    }

    a {
      text-decoration: underline;

      ${({ theme }) => AccordionLinkColorConfig[theme.siteType]}
    }
  `,

  List: styled.ul`
    padding: 0;
    margin: 0 0 13px 16px;
    line-height: 1.4;

    li {
      font-size: ${({ theme }) => theme.font.size[3]};
      ${({ theme }) => AccordionElementColorConfig[theme.siteType]}
    }

    &:last-of-type {
      margin-bottom: 0;
    }
  `,

  Link: styled(FesLink)`
    margin-bottom: ${({ theme }) => theme.spacing.y.xs};
    text-decoration: underline;
    text-transform: capitalize;

    ${({ theme }) => AccordionLinkColorConfig[theme.siteType]}
  `,
};

interface ProductDescriptionCareProps {
  title: string;
  materialsTitle: string;
  colorTitle: string;
  cleverCareLink: string;
}

const tagsRegExp = /(\{([^}]*)\})/g;
const tagRegExp = /^\{([^}]*)\}$/;

export const ProductDescriptionCare = ({
  title,
  materialsTitle,
  colorTitle,
  cleverCareLink,
}: ProductDescriptionCareProps): ReactElement => {
  const {
    product: {
      featuresIntro,
      detailsBullets,
      programTitle,
      programDescription,
      fabricBullets,
      color,
      washingLabels,
      code,
      fiberRanking,
    },
  } = usePDPContext();

  const tagList = {
    // {compositionMaterial}
    compositionMaterial: useMemo(
      () =>
        fiberRanking?.fiberRanking?.productFiberRankingData?.fibers
          ?.map(fiber => `${fiber?.percentage}% ${fiber?.descriptionShort}`)
          .join(', '),
      [fiberRanking]
    ),
  };

  const renderDynamicContent = (e: string) => {
    let text = e;
    const tags = text.match(tagsRegExp);

    if (tags) {
      tags.forEach(tag => {
        const [placeholder, key] = tagRegExp.exec(tag) || [];

        if (placeholder && key) {
          const content = tagList[key as keyof typeof tagList];

          if (content) {
            text = text.replace(placeholder, content);
          } else {
            text = '';
          }
        }
      });
    }

    return text;
  };

  return (
    <S.Wrapper>
      <h2 data-accordion-title>{title}</h2>
      {featuresIntro && (
        <p data-testid="features-intro" dangerouslySetInnerHTML={{ __html: featuresIntro }} />
      )}
      {Array.isArray(detailsBullets) && Boolean(detailsBullets.length) && (
        <S.List>
          {detailsBullets.map((e, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: e || '' }} />
          ))}
        </S.List>
      )}
      {programTitle && <h3 data-testid="program-title">{programTitle}</h3>}
      {programDescription && (
        <p data-testid="program-desc" dangerouslySetInnerHTML={{ __html: programDescription }} />
      )}
      {materialsTitle && <h3 data-testid="material-title">{materialsTitle}</h3>}
      {Array.isArray(fabricBullets) && Boolean(fabricBullets.length) && (
        <S.List>
          {fabricBullets.map((e, i) => {
            const text = renderDynamicContent(e as string);

            return text && <li key={i} dangerouslySetInnerHTML={{ __html: text }} />;
          })}
        </S.List>
      )}
      {colorTitle && color?.description && (
        <>
          <h3 data-testid="color-finish-title">{colorTitle}</h3>
          <S.Link href={getColorDetailUrl(color.description)} data-testid="color-finish-desc">
            {color?.description}
          </S.Link>
        </>
      )}
      {Array.isArray(washingLabels) && Boolean(washingLabels.length) && (
        <WashingLabels title="Washing Instructions" cleverCareLink={cleverCareLink} />
      )}
      <p data-testid="art-number">Art nr: {code}</p>
    </S.Wrapper>
  );
};
